import moment from 'moment';

import { getCountryAlpha2 } from '../countries';
import { resolveFieldValue } from '../field-paths';
import { compareDates, validateDateRangeCoverage } from './date-validation-utils';
import patternValidators from './pattern-validators';

// Helper to validate email format using standard email pattern validator
const validateWithEmailPattern = value => {
  if (!value || typeof value !== 'string') return true;
  const emailPattern = patternValidators.email.pattern.value;
  if (emailPattern.test(value.trim())) {
    return true;
  }
  return patternValidators.email.pattern.message;
};

const functionValidators = {
  dateAfter: {
    validate: (value, params, formValues, fieldPath) => {
      if (!value || !params?.dependsOnFields) return true;

      // Create context based on field path
      const context = {};
      const pathParts = fieldPath.split('.');

      if (pathParts.length === 3) {
        // We're in an array (e.g., "academic_qualifications.0.qualification_end_date")
        context.parentField = { code: pathParts[0] };
        context.arrayIndex = parseInt(pathParts[1]);
      }

      const compareValue = resolveFieldValue(params.dependsOnFields[0], formValues, context);
      if (compareValue == null || compareValue === '') return true; // skip if compareValue is null or empty

      return compareDates(value, compareValue, 'after');
    },
  },

  dateStrictlyAfter: {
    validate: (value, params, formValues, fieldPath) => {
      if (!value || !params?.dependsOnFields) return true;

      const context = {};
      const pathParts = fieldPath.split('.');

      if (pathParts.length === 3) {
        context.parentField = { code: pathParts[0] };
        context.arrayIndex = parseInt(pathParts[1], 10);
      }

      const compareValue = resolveFieldValue(params.dependsOnFields[0], formValues, context);
      if (compareValue == null || compareValue === '') return true;

      const date = moment(value, 'DD/MM/YYYY', true);
      const compareDate = moment(compareValue, 'DD/MM/YYYY', true);
      if (!date.isValid() || !compareDate.isValid()) return true;

      if (date.isAfter(compareDate)) {
        return true;
      }

      return (
        params?.errorMessage ??
        params?.error_message ??
        `Date must be after ${compareDate.format('DD/MM/YYYY')}`
      );
    },
  },

  addressInCountry: {
    validate: (value, params) => {
      if (!value || typeof value !== 'object') return true;

      const hasContent = [
        'street_address',
        'address_line_1',
        'locality',
        'suburb',
        'city',
        'region_state',
        'postal_code',
      ].some(key => {
        const part = value[key];
        return part != null && String(part).trim() !== '';
      });

      if (!hasContent) return true;

      const requiredCountry = params?.country ?? params?.mustBeCountry ?? 'AU';
      const countryCode = getCountryAlpha2(value.country);
      if (countryCode === requiredCountry) {
        return true;
      }

      return params?.errorMessage ?? params?.error_message ?? 'Address must be in Australia';
    },
  },

  dateWithinFutureWeeks: {
    validate: (value, params) => {
      if (!value) return true;

      const weeks = params?.weeks ?? params?.maxWeeks ?? params?.max_weeks;
      if (weeks == null || !Number.isFinite(Number(weeks))) return true;

      const date = moment(value, 'DD/MM/YYYY', true);
      if (!date.isValid()) return true;

      const today = moment().startOf('day');
      const maxDate = today.clone().add(Number(weeks), 'weeks');

      if (date.isBefore(today)) {
        return params?.errorMessage ?? params?.error_message ?? 'The date must be in the future';
      }

      if (date.isAfter(maxDate)) {
        const weekLabel = Number(weeks) === 1 ? '1 week' : `${weeks} weeks`;
        return (
          params?.errorMessage ??
          params?.error_message ??
          `The date must be within the next ${weekLabel}`
        );
      }

      return true;
    },
  },

  firstNameExcludesMiddleNames: {
    validate: (value, params, formValues, fieldPath) => {
      if (!value || typeof value !== 'string') return true;

      const namePattern = patternValidators.name.pattern;
      if (!namePattern.value.test(value)) {
        return namePattern.message;
      }

      const dependsOn = params?.dependsOnFields ?? params?.depends_on_fields;
      if (!dependsOn?.length) return true;

      const context = {};
      const pathParts = (fieldPath || '').split('.');
      if (pathParts.length === 3) {
        context.parentField = { code: pathParts[0] };
        context.arrayIndex = parseInt(pathParts[1], 10);
      }

      const middleRaw = resolveFieldValue(dependsOn[0], formValues, context);
      if (middleRaw == null || typeof middleRaw !== 'string' || middleRaw.trim() === '') {
        return true;
      }

      const normalizeNamePart = s => s.trim().replace(/\s+/g, ' ').toLowerCase();
      const first = normalizeNamePart(value);
      const middle = normalizeNamePart(middleRaw);
      if (!first || !middle) return true;

      if (first === middle || first.endsWith(` ${middle}`)) {
        return (
          params?.errorMessage ??
          params?.error_message ??
          'Please enter only your first name here. Do not include middle name(s) in this field.'
        );
      }

      return true;
    },
  },

  dobMatch: {
    externalDependencies: params => [params?.dependsOnFields?.[0] || 'dob'],
    validate: (value, params, formValues) => {
      // value is the ID's DOB (from extracted fields)
      // Compare with the candidate's DOB from formValues
      if (!value) return true; // Skip if no ID DOB

      const candidateDobField = params?.dependsOnFields?.[0] || 'dob';
      const candidateDob = formValues[candidateDobField];

      if (!candidateDob) return true; // Skip if no candidate DOB to compare

      // Parse both dates for comparison
      // Both dates should be in DD/MM/YYYY format from Regula and our date selector
      const idDob = moment(value, 'DD/MM/YYYY');
      const candDob = moment(candidateDob, 'DD/MM/YYYY');

      if (!idDob.isValid() || !candDob.isValid()) {
        return true; // Skip validation if dates are invalid
      }

      if (idDob.isSame(candDob, 'day')) {
        return true;
      }

      // Custom error message or default
      return (
        params?.errorMessage ||
        `The date of birth on your ID (${value}) does not match the date of birth you provided in the Personal Details section (${candidateDob}), please ensure your date of birth is correct. If you need to update your date of birth, go to the Personal Details section, update it there, and return to Verify Identity.`
      );
    },
  },

  // ------------ Wrapper validators ------------ //
  // No value is passed to these validators, just params and formValues

  addressHistory: {
    validate: (params, formValues) => {
      // Get current address from date
      if (!formValues.current_address_residence_from) {
        return 'Current address from date is required';
      }

      const currentAddressFrom = moment(formValues.current_address_residence_from, 'DD/MM/YYYY');
      if (!currentAddressFrom.isValid()) {
        return 'Current address from date is invalid';
      }
      // Calculate minimum past date (default 10 years)
      const yearsToCheck = params?.requiredAddressHistory || 10;
      const minPastDate = moment().subtract(yearsToCheck, 'years');

      // If current address covers entire period, we're done
      if (currentAddressFrom.isSameOrBefore(minPastDate)) {
        return true;
      }

      // Create current address range (from start date to now)
      const currentAddressRange = {
        from: currentAddressFrom,
        to: moment(),
      };

      // If we're here, we need to check previous addresses
      if (
        !Array.isArray(formValues.previous_addresses) ||
        formValues.previous_addresses.length === 0
      ) {
        return `Previous addresses are required to cover the last ${yearsToCheck} years`;
      }

      // Convert previous addresses to date ranges and include current address
      const dateRanges = [
        currentAddressRange,
        ...formValues.previous_addresses.map(address => ({
          from: moment(address.previous_address_residence_from, 'DD/MM/YYYY'),
          to: moment(address.previous_address_residence_to, 'DD/MM/YYYY'),
        })),
      ];

      const result = validateDateRangeCoverage({
        startDate: moment(),
        minPastDate,
        dateRanges,
        toleranceMonths: params?.toleranceMonths,
      });
      // If validation fails, return the error message string
      // If validation passes, return true
      if (result.isValid) {
        return true;
      }
      // Return the error message, or a default message if error is missing
      return result.error || 'Validation failed';
    },
  },

  addressesMustInclCountry: {
    validate: (params, formValues) => {
      const requiredCountry = params?.mustIncludeCountry;
      if (!requiredCountry) {
        return true;
      }

      if (formValues.residential_address?.country === requiredCountry) {
        return true;
      }

      if (formValues.postal_address?.country === requiredCountry) {
        return true;
      }

      const previousAddresses = formValues.previous_addresses;
      if (Array.isArray(previousAddresses)) {
        const hasRequiredCountry = previousAddresses.some(
          address => address.previous_address?.country === requiredCountry,
        );

        if (hasRequiredCountry) {
          return true;
        }
      }

      const errorMessage = `Your address history must include a ${requiredCountry} address`;
      return errorMessage;
    },
  },

  /**
   * Generic validator: trimmed lowercased value must not exactly equal any of the forbidden values (case-insensitive).
   * Use validation_params: { forbidden_values: string[], error_message?: string }.
   */
  forbidden_values: {
    validate: (value, params) => {
      if (!value || typeof value !== 'string') return true;
      const normalized = value.trim().toLowerCase();
      if (!normalized) return true;
      const list = params?.forbiddenValues ?? params?.forbidden_values ?? [];
      if (!Array.isArray(list) || list.length === 0) return true;
      for (const item of list) {
        if (item && typeof item === 'string' && normalized === item.trim().toLowerCase()) {
          return (
            params?.errorMessage ??
            params?.error_message ??
            'This value is invalid, please refer to the instructions above the input field'
          );
        }
      }
      return true;
    },
  },

  emailOrPhone: {
    validate: (value, params, formValues, fieldPath) => {
      const dependsOn = params?.dependsOnFields ?? params?.depends_on_fields ?? [];
      const context = {};
      const pathParts = (fieldPath || '').split('.');
      if (pathParts.length === 3) {
        context.parentField = { code: pathParts[0] };
        context.arrayIndex = parseInt(pathParts[1], 10);
      }

      const phone = dependsOn[0] ? resolveFieldValue(dependsOn[0], formValues, context) : null;
      const filled = entry => {
        if (entry == null) return false;
        if (typeof entry === 'string') return entry.trim() !== '';
        if (typeof entry === 'object') {
          const number = entry.number ?? entry.phone ?? entry.value;
          return typeof number === 'string' && number.trim() !== '';
        }
        return false;
      };

      if (!filled(value)) {
        if (filled(phone)) return true;
        return params?.errorMessage ?? params?.error_message ?? 'Enter an email or a phone number.';
      }

      return validateWithEmailPattern(value);
    },
  },

  includesCompanyDomain: {
    validate: (value, params, formValues) => {
      if (!value || typeof value !== 'string') return true;

      // Get allowed domains from params (backend resolves configuration paths into allowedDomains)
      // Ensure allowedDomains is always an array
      const allowedDomains = Array.isArray(params?.allowedDomains) ? params.allowedDomains : [];

      // If no domains configured, run standard email pattern validation
      if (!allowedDomains || allowedDomains.length === 0) {
        return validateWithEmailPattern(value);
      }

      // Extract domain from email
      const emailParts = value.trim().split('@');
      if (emailParts.length !== 2) {
        // Run standard email pattern validation for malformed emails
        return validateWithEmailPattern(value);
      }

      const emailDomain = emailParts[1].toLowerCase();

      // Normalize allowed domains (remove @ prefix if present, lowercase)
      const normalizedDomains = allowedDomains.map(d =>
        (d.startsWith('@') ? d.substring(1) : d).toLowerCase(),
      );

      // Check if email domain matches any allowed domain
      if (normalizedDomains.includes(emailDomain)) {
        return true;
      }

      const domainList = normalizedDomains.join(', ');
      return (
        params?.errorMessage || `Email must be from one of the following domains: ${domainList}`
      );
    },
  },
};

export const getValidatorDependencyCodes = (validatorName, params) => {
  const declared = functionValidators[validatorName]?.externalDependencies?.(params) || [];
  const configured = params?.dependsOnFields ?? params?.depends_on_fields ?? [];
  return [...new Set([...declared, ...configured])].filter(
    code => typeof code === 'string' && !code.includes('{'),
  );
};

export default functionValidators;
